import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useGetResourceShareQuery,
  useLazyGetPrincipalSharesQuery,
  useLazyGetResourceSharesQuery,
  type ResourceShare,
} from '@/store/api/resourceSharesApi';

type Lookup = { kind: 'resource' | 'principal'; value: string };

export function SharesPage() {
  const { shareId } = useParams<{ shareId?: string }>();
  const navigate = useNavigate();
  const [resourceArn, setResourceArn] = useState('');
  const [principalId, setPrincipalId] = useState('');
  const [lookup, setLookup] = useState<Lookup | null>(null);
  const [rows, setRows] = useState<ResourceShare[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadResource, resourceResult] = useLazyGetResourceSharesQuery();
  const [loadPrincipal, principalResult] = useLazyGetPrincipalSharesQuery();
  const {
    data: shareResult,
    isLoading: isLoadingShare,
    isError: isShareError,
    refetch: refetchShare,
  } = useGetResourceShareQuery(shareId ?? '', { skip: !shareId });
  const activeResult = lookup?.kind === 'resource' ? resourceResult : principalResult;

  async function search(kind: Lookup['kind'], value: string, nextOffset = 0) {
    if (!value.trim()) {
      return;
    }
    const nextLookup = { kind, value: value.trim() };
    setLookup(nextLookup);
    setOffset(nextOffset);
    if (nextOffset === 0) {
      setRows([]);
    }
    try {
      const result =
        kind === 'resource'
          ? await loadResource({
              resourceArn: value.trim(),
              limit: 25,
              offset: nextOffset,
            }).unwrap()
          : await loadPrincipal({
              principalId: value.trim(),
              limit: 25,
              offset: nextOffset,
            }).unwrap();
      setHasMore(result.shares.length === 25);
      setRows((previous) =>
        nextOffset === 0
          ? result.shares
          : [
              ...previous,
              ...result.shares.filter((item) => !previous.some((row) => row._id === item._id)),
            ],
      );
    } catch {
      if (nextOffset === 0) {
        setRows([]);
        setHasMore(false);
      }
    }
  }

  function showDetail(id: string) {
    navigate(`/shares/${id}`);
  }

  if (shareId) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/shares')}>
          <span aria-hidden="true">←</span>
          <span className="ml-2">Shares lookup</span>
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Resource share</h1>
          <p className="text-sm text-muted-foreground">Share detail · {shareId}</p>
        </div>
        {isLoadingShare ? (
          <LoadingRows label="Loading resource share detail" columns={2} rows={3} />
        ) : isShareError || !shareResult?.share ? (
          <div role="alert" className="rounded-md border border-destructive/40 p-4 text-sm">
            Could not load this share.{' '}
            <Button
              variant="link"
              onClick={() => {
                void refetchShare();
              }}
            >
              Try again
            </Button>
          </div>
        ) : (
          <Card>
            <CardContent className="pt-5">
              <dl className="grid gap-4 sm:grid-cols-2">
                {Object.entries(shareResult.share).map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
                    <dd className="mt-1 break-all text-sm">
                      {value === null ? '—' : String(value)}
                    </dd>
                  </div>
                ))}
              </dl>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Shares</h1>
        <p className="text-sm text-muted-foreground">
          Look up active resource shares by resource ARN or principal. There is no global share
          history.
        </p>
      </div>
      <div role="note" className="rounded-md border bg-muted/20 p-3 text-sm text-muted-foreground">
        This view is read-only. Grant and revoke controls are unavailable until resource-share
        mutations derive the actor from the authenticated session.
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <form
          className="space-y-2"
          onSubmit={(event) => {
            event.preventDefault();
            void search('resource', resourceArn);
          }}
        >
          <Label htmlFor="share-resource-arn">Resource ARN</Label>
          <div className="flex gap-2">
            <Input
              id="share-resource-arn"
              value={resourceArn}
              onChange={(e) => setResourceArn(e.target.value)}
              placeholder="Enter a resource ARN"
            />
            <Button type="submit" disabled={!resourceArn.trim() || resourceResult.isFetching}>
              Look up
            </Button>
          </div>
        </form>
        <form
          className="space-y-2"
          onSubmit={(event) => {
            event.preventDefault();
            void search('principal', principalId);
          }}
        >
          <Label htmlFor="share-principal-id">Principal ID</Label>
          <div className="flex gap-2">
            <Input
              id="share-principal-id"
              value={principalId}
              onChange={(e) => setPrincipalId(e.target.value)}
              placeholder="Enter a principal ID"
            />
            <Button type="submit" disabled={!principalId.trim() || principalResult.isFetching}>
              Look up
            </Button>
          </div>
        </form>
      </div>
      {activeResult.isFetching && rows.length === 0 ? (
        <LoadingRows label="Loading resource shares" columns={8} />
      ) : activeResult.isError && rows.length === 0 ? (
        <div role="alert" className="rounded-md border border-destructive/40 p-4 text-sm">
          Could not load shares for this lookup. Check the identifier and try again.
          <Button
            variant="link"
            onClick={() => lookup && void search(lookup.kind, lookup.value, offset)}
          >
            Try again
          </Button>
        </div>
      ) : lookup && rows.length === 0 ? (
        <EmptyState
          title="No active shares found"
          description="Only active, non-expired shares are returned by this lookup."
        />
      ) : rows.length > 0 ? (
        <>
          <p className="text-sm text-muted-foreground" role="status">
            {rows.length} active shares loaded
          </p>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto" role="region" aria-label="Resource shares table">
                <table className="w-full text-left text-sm">
                  <thead className="border-b bg-muted/40">
                    <tr>
                      {[
                        'Resource',
                        'Principal',
                        'Principal type',
                        'Role',
                        'Status',
                        'Expires',
                        'Granted by',
                        '',
                      ].map((label, index) => (
                        <th key={`${label}-${index}`} className="px-4 py-3 font-medium">
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((share) => (
                      <tr key={share._id} className="border-b last:border-0 hover:bg-muted/20">
                        <td className="max-w-sm break-all px-4 py-3 font-mono text-xs">
                          {share.resourceArn}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs">{share.principalId}</td>
                        <td className="px-4 py-3">{share.principalType}</td>
                        <td className="px-4 py-3">{share.role}</td>
                        <td className="px-4 py-3">active</td>
                        <td className="px-4 py-3">
                          {share.expiresAt
                            ? new Date(share.expiresAt).toLocaleString()
                            : 'No expiry'}
                        </td>
                        <td className="px-4 py-3">{share.grantedBy}</td>
                        <td className="px-4 py-3">
                          <Button
                            variant="link"
                            className="h-auto p-0"
                            onClick={() => showDetail(share._id)}
                          >
                            View details
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          {activeResult.isError && (
            <div role="alert" className="text-sm text-destructive">
              Could not load the next page. Existing results are preserved.{' '}
              <Button
                variant="link"
                onClick={() => lookup && void search(lookup.kind, lookup.value, offset)}
              >
                Try again
              </Button>
            </div>
          )}
          {hasMore && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                disabled={activeResult.isFetching}
                onClick={() => lookup && void search(lookup.kind, lookup.value, rows.length)}
              >
                {activeResult.isFetching ? 'Loading…' : 'Load more shares'}
              </Button>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
